import { Box, Button, Checkbox, Flex, HStack, Icon, Spinner, Text, useToast } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { FiCheckCircle, FiFileText } from "react-icons/fi";
import { acceptDocuments, getPendingDocuments, PlatformDocument } from "@lubbezposrednio-frontend/core";
import { DocumentHtml, DOC_TITLES } from "./documentShared";

/**
 * Blokująca bramka akceptacji — po zmianie wersji regulaminu/polityki wymusza ponowną zgodę
 * przy następnym wejściu zalogowanego użytkownika. Montowana w shellu (za logowaniem).
 *
 * Świadomie NIE używa Chakra Modal (portal) — montuje się obok DiscoverView, które ma własne
 * portale + infinite scroll; nakładające się portale powodowały crash removeChild w trakcie commitu.
 * Zwykły fixed-overlay jest odporny na ten wyścig.
 */
export const DocumentsGate: React.FC = () => {
  const toast = useToast();
  const [docs, setDocs] = useState<PlatformDocument[]>([]);
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let mounted = true;
    getPendingDocuments()
      .then(r => {
        if (!mounted) return;
        const pending = r.data.documents ?? [];
        if (pending.length) {
          setDocs(pending);
          setOpen(true);
        }
      })
      .catch(() => {
        /* brak/401 — nie blokujemy */
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Blokuje scroll tła, gdy bramka jest aktywna.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      await acceptDocuments();
      setOpen(false);
      toast({ title: "Dziękujemy za akceptację", status: "success", isClosable: true, duration: 2500 });
    } catch {
      toast({ title: "Nie udało się zapisać akceptacji", status: "error", isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  if (!open || !docs.length) return null;
  const current = docs[active];
  const multiple = docs.length > 1;

  return (
    <Flex position="fixed" inset="0" zIndex={3000} align="center" justify="center" bg="rgba(20,0,45,0.7)" backdropFilter="blur(4px)" p={{ base: "0", sm: "20px" }}>
      <Flex direction="column" bg="white" borderRadius={{ base: "0", sm: "22px" }} overflow="hidden" w="100%" maxW="720px" h={{ base: "100%", sm: "auto" }} maxH={{ base: "100%", sm: "86vh" }} boxShadow="0 24px 70px rgba(0,0,0,0.4)">
        <Box bgGradient="linear(135deg, #430098, #7b2ff7)" color="white" py="18px" px="24px" flexShrink={0}>
          <HStack spacing="11px">
            <Icon as={FiFileText} boxSize="22px" />
            <Box>
              <Text fontSize="18px" fontWeight="900">Zaktualizowaliśmy dokumenty</Text>
              <Text fontSize="13px" opacity={0.9} fontWeight="500">Prosimy o zapoznanie się i akceptację, aby kontynuować.</Text>
            </Box>
          </HStack>
        </Box>

        {multiple && (
          <HStack spacing="8px" px="22px" pt="16px" pb="4px" wrap="wrap" flexShrink={0}>
            {docs.map((d, i) => (
              <Button
                key={d.type}
                size="sm"
                onClick={() => setActive(i)}
                variant={i === active ? "solid" : "outline"}
                bg={i === active ? "primary" : "transparent"}
                color={i === active ? "white" : "primary"}
                borderColor="rgba(67,0,152,0.2)"
                borderRadius="full"
                fontWeight="700"
                _hover={{ bg: i === active ? "#360079" : "rgba(67,0,152,0.06)" }}
              >
                {DOC_TITLES[d.type] || d.title}
              </Button>
            ))}
          </HStack>
        )}

        <Box px="26px" py="18px" overflowY="auto" flex="1">
          {!multiple && <Text fontSize="20px" fontWeight="900" color="primary" mb="6px">{DOC_TITLES[current.type] || current.title}</Text>}
          <Text fontSize="12px" color="gray.400" mb="14px">Wersja {current.version}</Text>
          {current.content === null ? (
            <Flex justify="center" py="40px"><Spinner color="primary" /></Flex>
          ) : (
            <DocumentHtml html={current.content} />
          )}
        </Box>

        <Flex direction="column" gap="12px" p="20px 26px" borderTop="1px solid rgba(67,0,152,0.08)" flexShrink={0}>
          <Checkbox isChecked={accepted} onChange={e => setAccepted(e.target.checked)} colorScheme="purple" fontWeight="600">
            <Text fontSize="14px" color="gray.700">
              Oświadczam, że zapoznałem(-am) się z {multiple ? "powyższymi dokumentami" : `dokumentem „${DOC_TITLES[current.type] || current.title}”`} i akceptuję {multiple ? "ich" : "jego"} treść.
            </Text>
          </Checkbox>
          <Button onClick={submit} isLoading={saving} isDisabled={!accepted} bg="primary" color="white" borderRadius="12px" fontWeight="800" size="lg" leftIcon={<Icon as={FiCheckCircle} />} _hover={{ bg: "#360079" }}>
            Akceptuję i kontynuuję
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
